// src/components/Admin/tabs/StatsTab.jsx
import React, { useMemo } from 'react';
import { startOfDay, addDays, startOfWeek, startOfMonth, money, REVENUE_STATUSES } from '../adminUtils';
import { MiniBarChart, StatusBreakdown, KpiCard } from '../AdminWidgets';

const StatsTab = ({ bookings, dogs, rooms }) => {
  const metrics = useMemo(() => {
    const now = new Date();
    const today = startOfDay(now);
    const thisWeekStart = startOfWeek(now);
    const thisWeekEnd = addDays(thisWeekStart, 7);
    const nextWeekStart = thisWeekEnd;
    const nextWeekEnd = addDays(nextWeekStart, 7);
    const monthStart = startOfMonth(now);

    const activeBookings = bookings.filter((b) => b.status !== 'cancelled');
    const cancelledCount = bookings.length - activeBookings.length;
    const revenueBookings = bookings.filter((b) => REVENUE_STATUSES.includes(b.status));

    const checkInsInRange = (start, end) =>
      activeBookings.filter((b) => {
        const d = startOfDay(new Date(b.checkInDate));
        return d >= start && d < end;
      }).length;

    const checkInsThisWeek = checkInsInRange(thisWeekStart, thisWeekEnd);
    const checkInsNextWeek = checkInsInRange(nextWeekStart, nextWeekEnd);

    const totalRevenue = revenueBookings.reduce((sum, b) => sum + (b.totalPrice || 0), 0);
    const revenueThisMonth = revenueBookings
      .filter((b) => new Date(b.createdAt) >= monthStart)
      .reduce((sum, b) => sum + (b.totalPrice || 0), 0);
    const avgBookingValue = revenueBookings.length ? totalRevenue / revenueBookings.length : 0;

    const monthlyRevenue = Array.from({ length: 6 }).map((_, i) => {
      const mDate = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
      const mNext = new Date(mDate.getFullYear(), mDate.getMonth() + 1, 1);
      const value = revenueBookings
        .filter((b) => {
          const d = new Date(b.createdAt);
          return d >= mDate && d < mNext;
        })
        .reduce((sum, b) => sum + (b.totalPrice || 0), 0);
      return { label: mDate.toLocaleDateString('es-MX', { month: 'short' }), value };
    });

    const monthlyDogs = Array.from({ length: 6 }).map((_, i) => {
      const mDate = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
      const mNext = new Date(mDate.getFullYear(), mDate.getMonth() + 1, 1);
      const value = dogs.filter((d) => {
        if (!d.createdAt) return false;
        const created = new Date(d.createdAt);
        return created >= mDate && created < mNext;
      }).length;
      return { label: mDate.toLocaleDateString('es-MX', { month: 'short' }), value };
    });

    const dogsThisWeek = dogs.filter(
      (d) => d.createdAt && new Date(d.createdAt) >= thisWeekStart && new Date(d.createdAt) < thisWeekEnd
    ).length;

    const activeStaysToday = activeBookings.filter((b) => {
      const inD = startOfDay(new Date(b.checkInDate));
      const outD = startOfDay(new Date(b.checkOutDate));
      return inD <= today && outD > today;
    }).length;
    const occupancyRate = rooms.length ? Math.round((activeStaysToday / rooms.length) * 100) : 0;

    const statusCounts = bookings.reduce((acc, b) => {
      acc[b.status] = (acc[b.status] || 0) + 1;
      return acc;
    }, {});

    const roomDemand = {};
    bookings.forEach((b) => {
      const name = b.room?.name || 'Sin asignar';
      roomDemand[name] = (roomDemand[name] || 0) + 1;
    });
    const topRooms = Object.entries(roomDemand)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    const next7 = addDays(today, 7);
    // Solo reservas que aún no han hecho check-in - una vez que status pasa a
    // "checked-in" (o más adelante), ya no es un check-in "próximo".
    const upcomingCheckIns = activeBookings
      .filter((b) => {
        if (['checked-in', 'checked-out', 'completed'].includes(b.status)) return false;
        const d = startOfDay(new Date(b.checkInDate));
        return d >= today && d < next7;
      })
      .sort((a, b) => new Date(a.checkInDate) - new Date(b.checkInDate))
      .slice(0, 6);

    const cancellationRate = bookings.length ? Math.round((cancelledCount / bookings.length) * 100) : 0;

    return {
      checkInsThisWeek,
      checkInsNextWeek,
      totalRevenue,
      revenueThisMonth,
      avgBookingValue,
      monthlyRevenue,
      monthlyDogs,
      dogsThisWeek,
      occupancyRate,
      activeStaysToday,
      statusCounts,
      topRooms,
      upcomingCheckIns,
      cancellationRate,
      totalBookingsCount: bookings.length,
    };
  }, [bookings, dogs, rooms]);

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Check-ins esta semana"
          value={metrics.checkInsThisWeek}
          sublabel={`${metrics.checkInsNextWeek} programados la próxima semana`}
        />
        <KpiCard
          label="Ingresos acumulados"
          value={money(metrics.totalRevenue)}
          sublabel={`${money(metrics.revenueThisMonth)} este mes`}
          accent
        />
        <KpiCard
          label="Ticket promedio"
          value={money(metrics.avgBookingValue)}
          sublabel={`sobre ${metrics.totalBookingsCount} reservas`}
        />
        <KpiCard
          label="Ocupación hoy"
          value={`${metrics.occupancyRate}%`}
          sublabel={`${metrics.activeStaysToday} de ${rooms.length} cuartos ocupados`}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-[#3A2A20]/10 p-6">
          <h3 className="font-['Fraunces'] text-lg font-semibold text-[#3A2A20] mb-1">
            Ingresos por mes
          </h3>
          <p className="text-xs text-[#3A2A20]/50 mb-2">Últimos 6 meses, reservas confirmadas o completadas</p>
          <MiniBarChart data={metrics.monthlyRevenue} color="#9A0002" formatValue={(v) => money(v)} />
        </div>

        <div className="bg-white rounded-2xl border border-[#3A2A20]/10 p-6">
          <h3 className="font-['Fraunces'] text-lg font-semibold text-[#3A2A20] mb-1">
            Reservas por estatus
          </h3>
          <p className="text-xs text-[#3A2A20]/50 mb-4">
            {metrics.cancellationRate}% tasa de cancelación
          </p>
          <StatusBreakdown counts={metrics.statusCounts} total={metrics.totalBookingsCount} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-2xl border border-[#3A2A20]/10 p-6">
          <h3 className="font-['Fraunces'] text-lg font-semibold text-[#3A2A20] mb-1">
            Perros registrados por mes
          </h3>
          <p className="text-xs text-[#3A2A20]/50 mb-2">{metrics.dogsThisWeek} nuevos esta semana</p>
          <MiniBarChart data={metrics.monthlyDogs} color="#3A5A7A" />
        </div>

        <div className="bg-white rounded-2xl border border-[#3A2A20]/10 p-6">
          <h3 className="font-['Fraunces'] text-lg font-semibold text-[#3A2A20] mb-4">
            Cuartos más solicitados
          </h3>
          {metrics.topRooms.length === 0 ? (
            <p className="text-sm text-[#3A2A20]/40">Aún no hay datos suficientes.</p>
          ) : (
            <div className="space-y-3">
              {metrics.topRooms.map(([name, count]) => {
                const max = metrics.topRooms[0][1];
                return (
                  <div key={name}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-[#3A2A20]/70">{name}</span>
                      <span className="text-[#3A2A20]/50">{count}</span>
                    </div>
                    <div className="h-2 rounded-full bg-[#3A2A20]/5 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-[#B08D57]"
                        style={{ width: `${(count / max) * 100}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-[#3A2A20]/10 p-6">
          <h3 className="font-['Fraunces'] text-lg font-semibold text-[#3A2A20] mb-4">
            Próximos check-ins (7 días)
          </h3>
          {metrics.upcomingCheckIns.length === 0 ? (
            <p className="text-sm text-[#3A2A20]/40">Nada programado por ahora.</p>
          ) : (
            <ul className="space-y-3">
              {metrics.upcomingCheckIns.map((b) => (
                <li key={b._id} className="flex justify-between items-center text-sm">
                  <div>
                    <p className="font-medium text-[#3A2A20]">{b.user?.fullname || 'Usuario'}</p>
                    <p className="text-xs text-[#3A2A20]/50">{b.room?.name}</p>
                  </div>
                  <span className="text-xs font-medium text-[#9A0002]">
                    {new Date(b.checkInDate).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default StatsTab;